import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { toast } from "react-toastify";
import Text from "../renderer/Text";
import {
  evaluateSpeech,
  speechEvaluationDuration,
} from "../../store/quiz/quizBaseApi";
import { useStudy } from "../../pages/study/hooks";
import { sleep, generateUUID } from "../../utils";
import {
  getMicrophoneStream,
  getRecorderOptions,
  extensionForMimeType,
  hasKnownAudioContainer,
  createRunningAudioContext,
  waitForLiveAudioTrack,
  recordedAudioHasSound,
} from "../../utils/speechUtils";
import RecordingInactivity from "../study/RecordingInactivity";
import { makeStyles } from "tss-react/mui";
import { Box, Typography, Button } from "@mui/material";
import recordListenSvg from "../../assets/svgs/speechEvaluationIcons/recordListen.svg";
import recordMicSvg from "../../assets/svgs/speechEvaluationIcons/recordMic.svg";
import waveSvg from "../../assets/svgs/speechEvaluationIcons/wave.svg";
import wave2Svg from "../../assets/svgs/speechEvaluationIcons/wave2.svg";
import evalBorderSvg from "../../assets/svgs/speechEvaluationIcons/evalBorder.svg";
import replayMicSvg from "../../assets/svgs/speechEvaluationIcons/replayMic.svg";
import systemSpeakSvg from "../../assets/svgs/speechEvaluationIcons/systemSpeak.svg";
import readSvg from "../../assets/svgs/speechEvaluationIcons/read.svg";
import tickSvg from "../../assets/svgs/speechEvaluationIcons/tick.svg";


const useStyles = makeStyles()((theme) => ({
  highlight: {
    marginRight: theme.spacing(1),
    color: theme.palette.common.black,
    background: "rgba(255, 199, 74, 0.2)",
    border: "1px solid #FFC74A",
    borderRadius: "10px",
    padding: "8px",
    lineHeight: "2.6",
    fontSize: "1.1rem",
  },
  currentSentance: {
    fontSize: "1.1rem",
    fontWeight: "600",
    padding: "5px",
    borderRadius: "8px",
  },
  wrong: {
    fontSize: "1.1rem",
    border: "2px solid rgba(255, 74, 74, 1)",
    background: "rgba(255, 74, 74, 0.2)",
    padding: theme.spacing(2),
    borderRadius: "10px",
  },
  failedCount: {
    position: "absolute",
    top: "-12px",
    right: "-12px",
    background: "rgba(255, 0, 0, 1)",
    height: "28px",
    width: "28px",
    borderRadius: "50%",
    color: "white",
  },
}));

// Realtime long-pause (VAD) tuning. If the student goes silent for longer than
// LONG_PAUSE_MS *after* they've started reading, the recording is auto-stopped
// and flagged invalid (same treatment as the backend "silent" case) instead of
// being sent to /evaluate-speech.
const LONG_PAUSE_MS = 7000; // spoke-then-paused window
// If the student never produces real speech, auto-stop after this long from the
// start of recording (soft voice / dead mic) and show the "Are you Speaking?" copy.
const NO_SPEECH_TIMEOUT_MS = 7000;
// Time-domain amplitude thresholds (avg |sample - 128|). Two-threshold VAD with
// hysteresis so ambient/AGC noise doesn't defeat the pause timer:
//   > SPEECH_ONSET_THRESHOLD  => confirmed real speech; arms the long-pause timer.
//   > VOICE_ACTIVITY_THRESHOLD => sound present; resets the pause timer.
//   <= VOICE_ACTIVITY_THRESHOLD => counts as a pause and accumulates toward the limit.
// Calibrated for soft speakers / low-gain laptop mics: 20 was MicrophoneChecker's
// *confident-speech* bar, too high as a "has spoken at all" gate, so quiet readers
// (avg ~8-18) were misclassified as never-spoke and got a false "Are you Speaking?".
// The activity floor mirrors MicrophoneChecker's proven SILENCE_THRESHOLD = 8.
const VOICE_ACTIVITY_THRESHOLD = 8; // above this = not a pause (resets the timer)
const SPEECH_ONSET_THRESHOLD = 14; // above this = real speech; arms the timer
// A live mic in any real room reads small non-zero ambient; a dead/GC'd analyser
// reads a hard ~0. If the analyser never crosses this floor, we can't trust it, so
// the never-spoke auto-stop is suppressed (avoids a false "Are you Speaking?").
const SENSOR_ALIVE_FLOOR = 3;
// Liveness has to be CONTINUOUS, not latched. An analyser whose context was
// closed, whose source node was GC'd, or whose track went muted mid-recording
// feeds exact digital silence: every sample is 128, so `average` is exactly 0.
// A real microphone in even a silent room dithers above that, so a run of
// perfectly flat frames this long means the sensor died -- NOT that the student
// stopped reading. While stalled the auto-stop is suspended: MediaRecorder is
// still capturing fine, and a genuinely silent capture is caught at submit time
// by recordedAudioHasSound.
const SENSOR_STALL_MS = 2000;
// The pause timer must only count silence it actually OBSERVED. A frame gap
// longer than this means the rAF loop wasn't running (hidden/occluded tab, main
// thread stall, GC pause) -- we have no idea whether the student was speaking
// during it, so the silence window restarts instead of counting the whole gap.
const MAX_FRAME_GAP_MS = 500;

// Flip to true to log the live analyser average on the target machine and tune
// the thresholds above; leave false in normal operation (no behavior change).
const VAD_DEBUG = false;

// Copy for the RecordingInactivity modal, keyed by why it opened.
const INACTIVITY_COPY = {
  silent: {
    title: "Are you Speaking?",
    message:
      "We haven't heard anything from you this time. What would you like to do?",
  },
  longPause: {
    title: "You took a long pause",
    message:
      "We noticed you stopped reading for a while. What would you like to do?",
  },
};

const SpeechEvaluation = ({
  question,
  title,
  showTitle,
  showText,
  onFailureThresholdReached,
  dictionary = [],
  disabled = false,
  onComplete,
  onEvaluationStarted,
  onNetworkFailure,
  maxRetries = 3,
  // SENTENCE-level gate only: compared against completenessScore (which the
  // response destructures as `overall_score`). Per-word colour comes from
  // errorType and has no frontend threshold at all.
  passingScore = 40,
  error,
  importantWords = [],
  skipFailures,
  sampleRate,
  channelCount,
  prefetchedSentanceAudio = null,
}) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [evaluating, setEvaluating] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [evaluated, setEvaluated] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [failedWords, setFailedWords] = useState([]);
  const [localSuccessWords, setLocalSuccessWords] = useState([]);
  const [missedWords, setMissedWords] = useState([]);
  const [failures, setFailures] = useState(0);
  const prevFailures = useRef(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioUrls, setAudioUrls] = useState([]);
  const [hasAudioLoaded, setHasAudioLoaded] = useState(false);
  const [inactivity, setInactivityPrompt] = useState(false);
  // Which copy the inactivity modal shows: "silent" (backend) or "longPause".
  const [inactivityReason, setInactivityReason] = useState("silent");
  const [needsUserPlay, setNeedsUserPlay] = useState(false);

  const [sentanceAudio, setSentanceAudio] = useState(null);
  // Holds the live sentence Audio so the teardown effect can stop it deterministically.
  const sentanceAudioRef = useRef(null);
  // Guards against starting two concurrent sentence plays (StrictMode / dep churn).
  const sentancePlayStartedRef = useRef(false);
  // Watchdog for the correct-pronunciation clip: if it neither ends nor errors,
  // we still reset state + advance so hasAudioLoaded can't get stuck true (which
  // would hide every record button and strand the student after 3 failures).
  const pronunciationWatchdogRef = useRef(null);

  const {
    profile,
    setPaused,
    setFailed: globalSetFailed,
    setFailedWords: globalSetFailedWords,
    failedWords: globalFailedWords,
    setActiveWords,
    setSuccessWords,
    setFailedWordsForSentence,
    setSuccessWordsForSentence,
    setMissedWordsForSentence,
  } = useStudy();
  const { classes } = useStyles();
  const [currentWave, setCurrentWave] = useState(waveSvg);
  const svgRef = useRef(null);
  const words = React.useRef([]);
  const referenceId = React.useRef(generateUUID());

  // The one live recording, as an object rather than React state.
  //
  // Previously every recorder shared a single `audioChunks` ref and only the LAST
  // recorder reached `mediaRecorder` state. A second recorder starting (a
  // double-click races the async getUserMedia) wiped the first one's container
  // header from the shared array, and the first recorder — unreachable through
  // state — was never stopped, so it kept pushing chunks into that same array
  // every 100ms for the rest of the session. The uploads that produced were
  // headerless or two interleaved streams, which is precisely what ffmpeg
  // rejects with "Invalid data found when processing input".
  //
  // Shape: { recorder, stream, chunks, aborted }. Each recording owns its chunks;
  // a superseded or aborted session is torn down and never publishes a blob.
  const activeSessionRef = useRef(null);
  // True between the click and getUserMedia resolving. `recording` state can't
  // cover that window because setRecording(true) only runs after the await.
  const startingRef = useRef(false);
  // Real container the browser recorded, for the upload filename + diagnostics.
  const recorderMimeRef = useRef("");
  const chunkCountRef = useRef(0);
  const streamRef = useRef(null);
  const stopDelayRef = useRef(null);
  const timerRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  // Keep the WebAudio source (and a muted graph tail) referenced for the whole
  // recording so Chromium can't garbage-collect the MediaStreamAudioSourceNode
  // mid-recording, which silently kills the analyser (false "Are you Speaking?").
  const sourceRef = useRef(null);
  const zeroGainRef = useRef(null);
  // True once the analyser has shown any real signal this recording (see
  // SENSOR_ALIVE_FLOOR). Gates the never-spoke auto-stop.
  const analyserAliveRef = useRef(false);
  const audioPlayerRef = useRef(null);
  const currentText = useRef(null);
  const animationFrameIdRef = useRef(null);
  // Realtime long-pause detector state (see startRecording -> detectLongPause).
  // The detector's rAF id lives on the session object, not in a shared ref: two
  // overlapping sessions used to clobber each other's id here, so cancelling one
  // detector left the other running (and vice versa).
  const silenceStartRef = useRef(null);
  const hasSpokenRef = useRef(false);
  const longPauseRef = useRef(false);
  // Why the auto-stop fired: "longPause" (spoke then paused) or "silent" (never
  // spoke). Selects the RecordingInactivity copy via inactivityReason.
  const pauseReasonRef = useRef("longPause");
  // legacy guard used in older TTS/socket flows — keep as noop ref so leftover references don't crash

  // Every read of the sentence goes through this. Callers have handed down
  // non-strings (a boolean `false` from AsyncSpeechEvaluation's initial state
  // when a chapter resolved to no sentences), which crashed the submit path at
  // `question.text.match(...)` after the recording had already been taken.
  const questionText = useMemo(
    () => (typeof question?.text === "string" ? question.text : ""),
    [question?.text],
  );

  const geographicalType = useMemo(
    () => String(profile.geographicalType || "").toLowerCase(),
    [profile.geographicalType],
  );

  const isSentanceFailed = useMemo(() => {
    return failedWords.length < 2 && failedWords.includes(questionText);
  }, [failedWords, questionText]);

  const totalWords = useMemo(
    () => questionText.replaceAll("\n", " ").split(" ").length,
    [questionText],
  );

  const estimateReadTime = useMemo(() => {
    if (!questionText || !Object.keys(profile).length) return 0;

    const wordsPerSecond =
      speechEvaluationDuration[geographicalType] ||
      speechEvaluationDuration.rural;
    const words = questionText.split(" ");
    const wordCount = words.length;

    // Estimate reading time based on word count and words per second
    const estimatedTime = Math.round(wordCount / wordsPerSecond);

    // Add a buffer time of 45 seconds to account for pauses and errors
    return 45 + estimatedTime;
  }, [questionText, geographicalType, profile]);

  const handleCoursePause = useCallback(() => {
    setPaused(true);
    setInactivityPrompt(false);
  }, [setPaused]);

  const incrementFailures = useCallback(() => {
    setFailures((prev) => prev + 1);
  }, []);

  // Dismissing the inactivity modal via "Continue". In a skip-failures flow (the
  // diagnostic), also record the current attempt and advance so the passage can
  // always reach completion — otherwise a silent/long-pause on the LAST sentence
  // strands the student on a done-looking passage with no Continue button. Empty
  // outcome => this attempt scores 0 (placement test tolerates it); non-diagnostic
  // flows keep the re-read behavior (just close the modal).
  const handleInactivityContinue = useCallback(() => {
    setInactivityPrompt(false);
    if (skipFailures && onComplete && typeof onComplete === "function") {
      onComplete({}, audioBlob, failures);
    }
  }, [skipFailures, onComplete, audioBlob, failures]);

  const drawWaveform = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    const draw = () => {
      analyser.getByteTimeDomainData(dataArray);

      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      canvasCtx.fillStyle = "#fff";
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = "rgb(255, 0, 0)";

      canvasCtx.beginPath();
      const sliceWidth = canvas.width / dataArray.length;
      let x = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();

      requestAnimationFrame(draw);
    };

    draw();
  }, []);

  // Reads the live recorder from activeSessionRef rather than `mediaRecorder`
  // state: a recorder that never made it into state was previously impossible to
  // stop, and the state-derived closure went stale across renders. Depends on
  // nothing, so it is stable for every effect that lists it.
  const stopRecording = useCallback((immediate = false) => {
    const session = activeSessionRef.current;
    if (!session) return;

    // Stop THIS session's pause detector so the 1s trailing window can't trip it.
    if (session.rafId) {
      cancelAnimationFrame(session.rafId);
      session.rafId = null;
    }
    // Clear any previously scheduled delayed stop
    if (stopDelayRef.current) {
      clearTimeout(stopDelayRef.current);
      stopDelayRef.current = null;
    }

    const doStop = () => {
      stopDelayRef.current = null;
      try {
        if (session.recorder.state !== "inactive") session.recorder.stop();
      } catch (e) {
        /* already stopped */
      }
    };

    // update the UI before stopping
    setRecording(false);
    setElapsedTime(0);
    setEvaluating(true);

    if (immediate) {
      doStop();
    } else {
      // Keep recording for 1s so trailing words are captured
      stopDelayRef.current = setTimeout(doStop, 1000);
    }
  }, []);

  const formatTime = useCallback((time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, []);

  const startRecording = useCallback(
    (force = false) => {
      if (disabled) return;
      // Click while already recording = stop. `force` MUST be a real boolean: the
      // buttons used to pass their click handler straight to this callback, so
      // `force` received the MouseEvent, was always truthy, and this branch never
      // ran — every click started an extra recording instead.
      if (recording && !force) return stopRecording();
      // Second click before getUserMedia resolves. Without this the two recorders
      // fight over one microphone and one chunk buffer (see activeSessionRef).
      if (startingRef.current) return;
      startingRef.current = true;

      getMicrophoneStream()
        .then(async (stream) => {
          // Retire whatever was recording before this. A superseded recorder that
          // is left running holds the mic open and keeps emitting chunks.
          const previous = activeSessionRef.current;
          if (previous) {
            previous.aborted = true;
            // Kill its detector now rather than waiting for its (async) onstop:
            // a loop left running reads a dying analyser and can auto-stop the
            // recording that is replacing it.
            if (previous.rafId) {
              cancelAnimationFrame(previous.rafId);
              previous.rafId = null;
            }
            previous.detachTrack?.();
            previous.detachTrack = null;
            try {
              if (previous.recorder.state !== "inactive") previous.recorder.stop();
            } catch (e) {
              /* already stopped */
            }
            previous.stream.getTracks().forEach((track) => track.stop());
          }

          streamRef.current = stream;
          await waitForLiveAudioTrack(stream); // post-idle tracks can start muted
          // Ask for a container this browser really supports, so recorder.mimeType
          // tells us what we actually got instead of us guessing "audio/wav".
          const recorder = new MediaRecorder(stream, getRecorderOptions());

          // Chunks belong to THIS recording. Only the first one carries the
          // container header, so a buffer shared with another recorder produces a
          // file ffmpeg cannot demux.
          const chunks = [];
          // Claim the slot NOW, before the last await. This used to happen ~15
          // lines further down, after the audio graph had already been written
          // into the shared refs -- so a superseded recorder whose `onstop` landed
          // in that window still passed the `isCurrent` check below and tore down
          // (and close()d) the audio context of the recording that had just
          // replaced it. The analyser then read flat digital silence for the rest
          // of the read and the VAD auto-stopped a student who was speaking.
          const session = {
            recorder,
            stream,
            chunks,
            aborted: false,
            rafId: null,
            // Set while the OS/browser has muted the capture track: the analyser
            // is fed digital silence then, which is not the student's doing.
            sensorMuted: false,
            audioContext: null,
            source: null,
            zeroGain: null,
            analyser: null,
            detachTrack: null,
          };
          activeSessionRef.current = session;

          // Resumed context: a suspended one (idle/backgrounded tab) feeds the
          // analyser silence and wrongly trips the "no audio" detector.
          const audioContext = await createRunningAudioContext();

          // Superseded or aborted while that await was in flight: release
          // everything and publish nothing rather than racing the live session.
          if (session.aborted || activeSessionRef.current !== session) {
            if (activeSessionRef.current === session) {
              activeSessionRef.current = null;
            }
            try {
              audioContext.close();
            } catch (e) {
              /* already closed */
            }
            try {
              if (recorder.state !== "inactive") recorder.stop();
            } catch (e) {
              /* never started */
            }
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          const analyser = audioContext.createAnalyser();
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
          // Retain the source (Chromium GCs an un-referenced MediaStreamAudioSource
          // mid-recording -> analyser goes silent) and keep the graph pulling audio
          // through a muted tail to the destination (inaudible; MediaRecorder still
          // records the raw stream, so no double audio).
          const zeroGain = audioContext.createGain();
          zeroGain.gain.value = 0;
          source.connect(zeroGain);
          zeroGain.connect(audioContext.destination);
          sourceRef.current = source;
          zeroGainRef.current = zeroGain;

          analyser.fftSize = 2048;
          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);

          audioContextRef.current = audioContext;
          analyserRef.current = analyser;
          dataArrayRef.current = dataArray;

          // Own the graph on the session too, so teardown never has to trust the
          // shared refs (which by then may belong to a newer recording).
          session.audioContext = audioContext;
          session.source = source;
          session.zeroGain = zeroGain;
          session.analyser = analyser;

          // A capture track the browser/OS mutes mid-recording (device grabbed by
          // another app, bluetooth switch, driver hiccup after repeated
          // open/close cycles) delivers digital silence. waitForLiveAudioTrack
          // only covers the START of the recording, so watch it for the duration
          // and suspend the auto-stop while it is muted.
          const track = stream.getAudioTracks()[0];
          if (track) {
            const onMute = () => {
              session.sensorMuted = true;
              console.warn("SPEECH | capture track muted mid-recording");
            };
            const onUnmute = () => {
              session.sensorMuted = false;
              silenceStartRef.current = null; // nothing observed while muted
            };
            track.addEventListener("mute", onMute);
            track.addEventListener("unmute", onUnmute);
            session.detachTrack = () => {
              track.removeEventListener("mute", onMute);
              track.removeEventListener("unmute", onUnmute);
            };
          }

          drawWaveform();

          // Realtime long-pause detection. Reuses the analyser above: each frame
          // we measure average amplitude. Once the student has actually started
          // reading (SPEECH_ONSET_THRESHOLD), a continuous silence longer than
          // LONG_PAUSE_MS auto-stops the recording and flags it invalid (handled
          // in sendAudio via longPauseRef). Arming only after speech avoids
          // penalising a slow start; a never-spoken recording is still caught by
          // the backend "silent" flag.
          hasSpokenRef.current = false;
          silenceStartRef.current = null;
          longPauseRef.current = false;
          pauseReasonRef.current = "longPause";
          analyserAliveRef.current = false;
          // Wall clock of the previous frame, and the start of the current run of
          // perfectly flat (average === 0) frames. Both are per-recording locals:
          // the detector needs to know not just what it read, but whether it was
          // running at all and whether the sensor was still feeding it.
          let lastFrameTs = Date.now();
          let flatSinceTs = null;
          const detectLongPause = () => {
            const now = Date.now();
            const frameGap = now - lastFrameTs;
            lastFrameTs = now;

            analyser.getByteTimeDomainData(dataArray);
            let sum = 0;
            for (let i = 0; i < bufferLength; i++) {
              sum += Math.abs(dataArray[i] - 128);
            }
            const average = sum / bufferLength;

            // Continuous liveness. `average === 0` means every sample was exactly
            // 128 -- digital silence, which a live mic does not produce even in a
            // quiet room, but a closed context / GC'd source / muted track does.
            if (average > 0) flatSinceTs = null;
            else if (flatSinceTs === null) flatSinceTs = now;
            const sensorStalled =
              session.sensorMuted ||
              (flatSinceTs !== null && now - flatSinceTs > SENSOR_STALL_MS);

            // The analyser is producing real signal (not a dead/GC'd node).
            if (average > SENSOR_ALIVE_FLOOR) analyserAliveRef.current = true;
            if (average > SPEECH_ONSET_THRESHOLD) hasSpokenRef.current = true;

            if (average > VOICE_ACTIVITY_THRESHOLD) {
              // Sound present -> not a pause; reset the timer (hysteresis: only
              // clearly-audible frames reset it, so ambient/AGC noise between the
              // two thresholds still counts as a pause and keeps accumulating).
              silenceStartRef.current = null;
            } else if (sensorStalled || frameGap > MAX_FRAME_GAP_MS) {
              // We have no evidence about this stretch, so it cannot count as a
              // pause: either the sensor stopped feeding us (stalled/muted) or the
              // loop itself wasn't running (hidden tab, main-thread stall, GC).
              // Restart the window -- the old code counted the whole wall-clock
              // gap as silence and auto-stopped students who were mid-sentence.
              silenceStartRef.current = null;
              if (VAD_DEBUG) {
                console.debug(
                  `[VAD] ignored frame avg=${average.toFixed(1)} gap=${frameGap} stalled=${sensorStalled}`,
                );
              }
            } else {
              if (silenceStartRef.current === null) {
                silenceStartRef.current = now;
              }
              // Armed (spoke then paused) uses LONG_PAUSE_MS; never-spoke uses
              // NO_SPEECH_TIMEOUT_MS from the start of the silent run.
              const limit = hasSpokenRef.current
                ? LONG_PAUSE_MS
                : NO_SPEECH_TIMEOUT_MS;
              const silentFor = now - silenceStartRef.current;

              if (VAD_DEBUG) {
                console.debug(
                  `[VAD] avg=${average.toFixed(1)} spoke=${hasSpokenRef.current} silentFor=${silentFor} limit=${limit}`,
                );
              }

              // The auto-stop only fires if the analyser has proven it can hear
              // (SENSOR_ALIVE_FLOOR) -- and, via the branch above, only while it
              // is STILL feeding us data. Liveness latched once was the bug: a
              // student who spoke the first few words and then lost the analyser
              // (context closed under them, source GC'd, track muted) was stopped
              // LONG_PAUSE_MS later even though MediaRecorder was still capturing
              // their voice the whole time.
              const canFire = hasSpokenRef.current || analyserAliveRef.current;

              if (silentFor > limit && canFire) {
                longPauseRef.current = true;
                pauseReasonRef.current = hasSpokenRef.current
                  ? "longPause"
                  : "silent";
                session.rafId = null;
                // Mirror stopRecording's UI updates, but stop immediately (no
                // trailing delay) since the tail is just more silence.
                setRecording(false);
                setElapsedTime(0);
                setEvaluating(true);
                recorder.stop();
                return;
              }
            }

            session.rafId = requestAnimationFrame(detectLongPause);
          };
          session.rafId = requestAnimationFrame(detectLongPause);

          recorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };
          recorder.onstop = () => {
            // Always release this session's own microphone.
            stream.getTracks().forEach((track) => track.stop());

            const isCurrent = activeSessionRef.current === session;
            if (isCurrent) activeSessionRef.current = null;

            // Teardown is scoped to THIS session's own nodes, never to the shared
            // refs: onstop fires asynchronously, so a superseded session arriving
            // here would otherwise disconnect and close() the audio graph of the
            // recording that replaced it -- leaving the live analyser reading flat
            // digital silence and the VAD stopping a student who was speaking.
            session.detachTrack?.();
            session.detachTrack = null;
            if (session.rafId) {
              cancelAnimationFrame(session.rafId);
              session.rafId = null;
            }
            if (isCurrent && animationFrameIdRef.current) {
              cancelAnimationFrame(animationFrameIdRef.current);
              animationFrameIdRef.current = null;
            }

            try {
              session.source?.disconnect();
              session.zeroGain?.disconnect();
              session.analyser?.disconnect();
            } catch (e) {
              /* nodes may already be detached */
            }
            if (session.audioContext) {
              try {
                session.audioContext.close();
              } catch (e) {
                /* already closed */
              }
            }
            // Only clear the shared refs if they still describe THIS session.
            if (sourceRef.current === session.source) sourceRef.current = null;
            if (zeroGainRef.current === session.zeroGain) zeroGainRef.current = null;
            if (analyserRef.current === session.analyser) analyserRef.current = null;
            if (audioContextRef.current === session.audioContext) {
              audioContextRef.current = null;
            }
            session.source = null;
            session.zeroGain = null;
            session.analyser = null;
            session.audioContext = null;

            // Aborted (question changed mid-read) or superseded by a newer
            // recording: publish nothing. Setting audioBlob is what auto-submits,
            // and what this session holds is a partial capture that would only
            // fail server-side decoding.
            if (session.aborted || !isCurrent) {
              return;
            }

            const blob = new Blob(chunks, {
              type: recorder.mimeType || "audio/webm",
            });
            recorderMimeRef.current = recorder.mimeType || "";
            chunkCountRef.current = chunks.length;
            setAudioBlob(blob);
            setAudioURL(URL.createObjectURL(blob)); // Set the audio URL for playback
            audioPlayerRef.current?.load(); // Reload the audio element for playback
          };
          recorder.start(100); // Collect 100ms chunks of audio
          setRecording(true);
          setElapsedTime(0); // Reset timer
          setAudioBlob(null);
        })
        .catch((error) =>
          toast.error("Error accessing microphone: " + error.message),
        )
        .finally(() => {
          startingRef.current = false;
        });
    },
    [
      recording,
      disabled,
      sampleRate,
      channelCount,
      drawWaveform,
      stopRecording,
    ],
  );

  // Updated code - Fixed indexing issue
  const sendAudio = useCallback(async () => {
    if (!audioBlob || !audioBlob.size || disabled) return;

    // No reference sentence => nothing to score against. Bail before the
    // tokenizing below (which used to throw on a non-string) and before any
    // upload, so the student gets a real message instead of a dead spinner.
    if (!questionText) {
      setEvaluating(false);
      console.error("SPEECH | no sentence text to evaluate against", {
        index: question.index,
        received: question.text,
      });
      toast.error(
        "This session has no reading text. Please contact your teacher.",
      );
      return;
    }

    // Realtime detector fired: either a >5s mid-reading pause ("longPause") or a
    // never-spoke recording ("silent"). Treat as an invalid attempt (same as the
    // backend "silent" outcome) and skip /evaluate-speech; the modal copy is
    // chosen from pauseReasonRef.
    if (longPauseRef.current) {
      longPauseRef.current = false;
      const pauseTokens = questionText.match(/\S+/g) || [];
      setEvaluating(false);
      incrementFailures();
      setFailedWords(pauseTokens);
      setInactivityReason(pauseReasonRef.current);
      setInactivityPrompt(true);
      globalSetFailed(true);
      globalSetFailedWords(pauseTokens.map((w, i) => ({ word: w, index: i })));
      setFailed(true);
      setFailedWordsForSentence({
        index: question.index,
        words: pauseTokens.map((w, i) => ({ word: w, index: i })),
      });
      setSuccessWordsForSentence({ index: question.index, words: [] });
      setMissedWordsForSentence({ index: question.index, words: [] });
      return;
    }

    // Decide "no audio" from what was ACTUALLY recorded, not the live analyser
    // (which can read silence from a suspended/warming-up AudioContext even
    // though MediaRecorder captured real audio -- the false "no audio" on Done).
    // Only a genuinely silent/dead-mic recording is blocked; anything with sound
    // proceeds to /evaluate-speech.
    if (!(await recordedAudioHasSound(audioBlob))) {
      setEvaluating(false);
      toast.error(
        "We couldn't detect any audio from your microphone. Please check that it's enabled and try again.",
      );
      return;
    }

    // A recording with no container header can only fail on the server (ffmpeg
    // answers "Invalid data found when processing input"), so stop it here and
    // tell the student something they can act on. This should now be
    // unreachable — it is a backstop for a capture path we haven't found yet,
    // and the log line is what would identify it.
    if (!(await hasKnownAudioContainer(audioBlob))) {
      setEvaluating(false);
      console.error("SPEECH | refusing to upload a headerless recording", {
        bytes: audioBlob.size,
        blobType: audioBlob.type,
        recorderMime: recorderMimeRef.current,
        chunks: chunkCountRef.current,
      });
      toast.error(
        "Your recording didn't save properly. Please record again.",
      );
      return;
    }

    setEvaluating(true);
    setFailed(false);
    setNetworkError(false);
    setFailedWords([]);
    setLocalSuccessWords([]);
    setMissedWords([]);
    globalSetFailed(false);
    globalSetFailedWords([]);
    setSuccessWords([]);

    if (onEvaluationStarted && typeof onEvaluationStarted === "function") {
      onEvaluationStarted(question.index);
    }

    const formData = new FormData();
    // Name the file after what it actually contains. Every upload used to be
    // called "audio.wav" while holding WebM/Opus, which made the server's
    // extension whitelist meaningless and every diagnostic misleading.
    formData.append(
      "student_answer",
      audioBlob,
      `audio${extensionForMimeType(audioBlob.type)}`,
    );
    formData.append("correct_text", questionText);
    // Diagnostics: lets a server-side log line distinguish "the browser produced
    // a bad blob" from "the upload was corrupted in transit".
    formData.append("recorder_mime", recorderMimeRef.current || "");
    formData.append("chunk_count", String(chunkCountRef.current));
    formData.append("blob_bytes", String(audioBlob.size));
    // Tokenize question text (preserve punctuation)
    const tokens = questionText.match(/\S+/g) || [];
    const question_words = tokens;

    const normalize = (s = "") => s.replace(/[^a-zA-Z]/g, "").toLowerCase();

    evaluateSpeech(formData)
      .then((data) => {
        const outcome = data.response || {};
        setEvaluated(outcome);

        if (!Object.keys(outcome).length) return;

        let {
          completenessScore: overall_score,
          words,
          reverseReading,
          randomOrderReading,
          silent,
        } = outcome;

        // randomOrderReading: the words were all read, but shuffled. The server
        // scores the whole sentence zero for this, same as reverseReading (its
        // ordered special case), so it fails here on the same path. Older
        // backends and the azure provider omit the flag, which reads as falsy.
        if (silent || reverseReading || randomOrderReading) {
          incrementFailures();
          setFailedWords(question_words);
          setInactivityReason("silent");
          setInactivityPrompt(silent);
          globalSetFailed(true);
          globalSetFailedWords(
            question_words.map((w, i) => ({ word: w, index: i })),
          );
          setFailed(true);

          setFailedWordsForSentence({
            index: question.index,
            words: question_words.map((w, i) => ({ word: w, index: i })),
          });
          setSuccessWordsForSentence({
            index: question.index,
            words: [],
          });
          setMissedWordsForSentence({
            index: question.index,
            words: [],
          });

          return;
        }

        /* ================================
           FAIL PATH (overall score failed)
           ================================ */
        if (overall_score < passingScore && !skipFailures) {
          console.log("less than passing score");
          globalSetFailed(true);

          const failedWordsStructured = [];
          const successWordsStructured = [];
          const missedWordsStructured = [];
          const matchedWordIndices = new Set();

          let refIdx = 0;
          words.forEach((w) => {
            if (w.errorType === "Insertion") return;
            const entry = {
              word: question_words[refIdx],
              index: refIdx,
            };
            refIdx++;

            // Colour is the backend's call, not ours. errorType is the single
            // source of truth; the frontend no longer re-derives red/green from
            // accuracyScore against a threshold of its own. Two thresholds for
            // one decision meant the API could call a word correct while the UI
            // painted it red, and tuning either one silently moved the other.
            // The backend knob is PRONUNCIATION_WORD_OK.
            if (w.errorType === "Omission") {
              missedWordsStructured.push(entry);
            } else if (w.errorType === "Mispronunciation") {
              failedWordsStructured.push(entry);
            } else {
              successWordsStructured.push(entry);
            }
          });

          setLocalSuccessWords(successWordsStructured);
          setSuccessWords(successWordsStructured);
          setFailedWords(failedWordsStructured.map((w) => w.word));
          setMissedWords(missedWordsStructured);
          globalSetFailedWords(failedWordsStructured);

          // Store words history for this sentence for navigation
          setFailedWordsForSentence({
            index: question.index,
            words: failedWordsStructured,
          });
          setSuccessWordsForSentence({
            index: question.index,
            words: successWordsStructured,
          });
          setMissedWordsForSentence({
            index: question.index,
            words: missedWordsStructured,
          });

          incrementFailures();
          setFailed(true);
          return;
        }

        const successArray = [];
        const failureArray = [];
        const failureArray2 = [];
        const missedArray = [];

        let refIdx = 0;
        words.forEach((w) => {
          if (w.errorType === "Insertion") return;
          const wordText = question_words[refIdx];
          const entry = { word: wordText, index: refIdx };
          refIdx++;

          // Backend owns the verdict -- see the note on the fail path above.
          if (w.errorType === "Omission") {
            missedArray.push(entry);
          } else if (w.errorType === "Mispronunciation") {
            failureArray.push(wordText);
            failureArray2.push(entry);
          } else {
            successArray.push(entry);
          }
        });

        console.log(failureArray2, successArray, missedArray);

        // Always store words history for navigation (regardless of skipFailures)
        setFailedWordsForSentence({
          index: question.index,
          words: failureArray2,
        });
        setSuccessWordsForSentence({
          index: question.index,
          words: successArray,
        });
        setMissedWordsForSentence({
          index: question.index,
          words: missedArray,
        });

        // A retry is spent on ANY flagged word -- mispronunciation or omission.
        // Both are the backend's verdict now: the frontend has no threshold of
        // its own, so "below the threshold" means errorType came back
        // Mispronunciation, which is mean_score < PRONUNCIATION_WORD_OK.
        //
        // Known cost, measured against Azure on 549 clips from ten production
        // schools at word_ok=0.20: 55.5% of readings Azure scored as complete
        // still burn an attempt, and 17.1% exhaust all three -- students who
        // read every word correctly, blocked. Lowering word_ok softens it but
        // does not fix it (5.6% blocked even at 0.02, because exp(GOP) has a
        // long tail near zero), and mispronunciation recall is only ~10% across
        // that whole range. Tune with PRONUNCIATION_WORD_OK; measure with
        // `manage.py evaluate_speech_corpus`.
        const retryWorthy = failureArray.length > 0 || missedArray.length > 0;
        if (retryWorthy && !skipFailures) {
          setLocalSuccessWords(successArray);
          setSuccessWords(successArray);
          setMissedWords(missedArray);
          globalSetFailedWords(failureArray2);

          incrementFailures();
          setFailedWords(failureArray);
          return;
        }

        // When skipFailures is true, still update global failed/success words
        // so AsyncTextToSpeech2 can highlight the active sentence correctly.
        if (skipFailures && (failureArray2.length || missedArray.length)) {
          globalSetFailedWords(failureArray2);
          setFailedWords(failureArray);
          setMissedWords(missedArray);
        }

        // Success or skipFailures case
        if (successArray.length) {
          setLocalSuccessWords(successArray);
          setSuccessWords(successArray);
        }

        if (onComplete && typeof onComplete === "function") {
          onComplete(outcome, audioBlob, failures);
        }

        // Clear global failed/success words after completion — per-sentence
        // history already stores the results, so global state is no longer
        // needed and leaving it set causes stale data to leak into other
        // sentences via fallback reads.
        globalSetFailedWords([]);
        setSuccessWords([]);

        setFailed(false);
        globalSetFailed(false);
        setFailures(0);
      })
      .catch((error) => {
        console.error("SPEECH | evaluate-speech failed", error);

        // utils/api.js throws the parsed JSON body on a non-2xx, so a curated
        // server message arrives as {status: "error", message}. It can also throw
        // a SyntaxError (non-JSON body) or a TypeError (network down), whose
        // .message is developer noise — hence the status check rather than
        // trusting .message. The old error.response.data.info.detail branch was
        // axios-shaped and could never match.
        const serverMessage =
          error?.status === "error" && typeof error?.message === "string"
            ? error.message
            : null;

        toast.error(
          serverMessage ||
            "We could not check your reading just now. Please try again.",
        );

        if (onNetworkFailure && typeof onNetworkFailure === "function") {
          onNetworkFailure();
        }

        setNetworkError(true);
      })
      .finally(() => {
        setEvaluating(false);
      });
  }, [
    audioBlob,
    disabled,
    question.index,
    questionText,
    passingScore,
    skipFailures,
    importantWords,
    failures,
    onEvaluationStarted,
    onComplete,
    onNetworkFailure,
    globalSetFailed,
    globalSetFailedWords,
    setSuccessWords,
    incrementFailures,
    setFailedWordsForSentence,
    setSuccessWordsForSentence,
  ]);

  // Play the fetched full-sentence TTS audio so the student hears the correct
  // pronunciation. Single playback path: driven by the effect below once the URL is set.
  const playSentanceAudio = useCallback(
    (url, evaluated, audioBlob, failures) => {
      if (!url) return;

      // Stop any prior sentence audio so two clips never overlap.
      if (sentanceAudioRef.current) {
        sentanceAudioRef.current.pause();
        sentanceAudioRef.current.onended = null;
        sentanceAudioRef.current.onerror = null;
        sentanceAudioRef.current = null;
      }
      if (pronunciationWatchdogRef.current) {
        clearTimeout(pronunciationWatchdogRef.current);
        pronunciationWatchdogRef.current = null;
      }

      const sentance = new Audio(url);
      sentanceAudioRef.current = sentance;

      // Clear playback state and advance to the next sentence. This is the ONLY
      // path that resets hasAudioLoaded, so it must run whether the clip finishes
      // normally (onended), fails (onerror), or hangs (watchdog) — otherwise
      // hasAudioLoaded stays stuck true and every record button stays hidden.
      let settled = false;
      const settleAndAdvance = () => {
        if (settled) return;
        settled = true;
        if (pronunciationWatchdogRef.current) {
          clearTimeout(pronunciationWatchdogRef.current);
          pronunciationWatchdogRef.current = null;
        }
        setIsPlaying(false);
        setHasAudioLoaded(false);
        setAudioUrls([]);
        setSentanceAudio(null);
        setAudioLoading(false);
        setActiveWords([]);
        globalSetFailedWords([]);
        setSuccessWords([]);
        setNeedsUserPlay(false);
        sentanceAudioRef.current = null;
        sentancePlayStartedRef.current = false;

        if (onComplete && typeof onComplete === "function") {
          sleep(500).then(() => onComplete(evaluated, audioBlob, failures));
        }
      };

      // stop the player and clear playback state so the user can re-record
      sentance.onended = settleAndAdvance;
      // A load/decode error can fire AFTER play() resolves — without this the
      // student would be stranded with hasAudioLoaded stuck true.
      sentance.onerror = settleAndAdvance;
      // Last-resort watchdog for a clip that neither ends nor errors (throttled
      // tab, silent stall). Bounded well above any real sentence clip.
      pronunciationWatchdogRef.current = setTimeout(settleAndAdvance, 15000);

      // Keep the highlighted words visible while the sentence is being read;
      // they clear in settleAndAdvance.
      const playPromise = sentance.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((err) => {
          if (err && err.name === "AbortError") {
            // Media was removed/paused (e.g. question changed) mid-play — expected.
            // Cancel the watchdog; the question-change teardown handles cleanup.
            if (pronunciationWatchdogRef.current) {
              clearTimeout(pronunciationWatchdogRef.current);
              pronunciationWatchdogRef.current = null;
            }
            return;
          }
          if (err && err.name === "NotAllowedError") {
            // Autoplay blocked (no fresh user gesture). Offer a tap-to-play fallback
            // via the (non-collapsing) replay button. Keep hasAudioLoaded/the URL so
            // the button shows; cancel the watchdog so we wait for the user's tap
            // instead of auto-advancing past the pronunciation they haven't heard.
            if (pronunciationWatchdogRef.current) {
              clearTimeout(pronunciationWatchdogRef.current);
              pronunciationWatchdogRef.current = null;
            }
            setNeedsUserPlay(true);
            setIsPlaying(false);
            return;
          }
          // Unknown play() failure — don't strand the student.
          console.error("Error playing sentence audio:", err);
          settleAndAdvance();
        });
      }
    },
    [onComplete, globalSetFailedWords, setSuccessWords, setActiveWords],
  );

  useEffect(() => {
    if (failures < maxRetries) return;
    if (!sentanceAudio) return;
    if (sentancePlayStartedRef.current) return; // double-fire guard

    sentancePlayStartedRef.current = true;
    setHasAudioLoaded(true);
    playSentanceAudio(sentanceAudio, evaluated, audioBlob, failures);
  }, [sentanceAudio, failures]);

  // Stop the recprding when the elapsed time exceeds the estimated reading time.
  // estimateReadTime is 0 only when there is no sentence to time; without the
  // guard the very first tick satisfied `1 >= 0` and auto-submitted at 00:00.
  // For any real sentence it is always 45 + n, so the auto-stop is unchanged.
  useEffect(() => {
    if (estimateReadTime > 0 && elapsedTime && elapsedTime >= estimateReadTime) {
      stopRecording();
    }
  }, [elapsedTime]);

  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [recording]);

  useEffect(() => {
    sendAudio();
  }, [audioBlob]);

  // Unmount cleanup: stop any live pronunciation clip and cancel its watchdog so
  // neither fires state updates / a stale advance after this component is gone.
  useEffect(() => {
    return () => {
      if (sentanceAudioRef.current) {
        sentanceAudioRef.current.pause();
        sentanceAudioRef.current.onended = null;
        sentanceAudioRef.current.onerror = null;
        sentanceAudioRef.current = null;
      }
      if (pronunciationWatchdogRef.current) {
        clearTimeout(pronunciationWatchdogRef.current);
        pronunciationWatchdogRef.current = null;
      }
      // Release the microphone if we unmount mid-recording. Marked aborted first
      // so onstop can't call setState on an unmounted component.
      const session = activeSessionRef.current;
      if (session) {
        session.aborted = true;
        if (session.rafId) {
          cancelAnimationFrame(session.rafId);
          session.rafId = null;
        }
        session.detachTrack?.();
        session.detachTrack = null;
        try {
          if (session.recorder.state !== "inactive") session.recorder.stop();
        } catch (e) {
          /* already stopped */
        }
        session.stream.getTracks().forEach((track) => track.stop());
        activeSessionRef.current = null;
      }
      if (stopDelayRef.current) {
        clearTimeout(stopDelayRef.current);
        stopDelayRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const audioElement = audioPlayerRef.current;
    if (audioElement) {
      const handleEnded = () => {
        setIsPlaying(false);

        if (audioUrls.length) {
          let _word = audioPlayerRef.current.getAttribute("text");
          setAudioUrls((prev) => {
            let index = audioUrls.findIndex((e) => e.word == _word);
            prev[index] = { ...prev[index], playing: false };
            return prev;
          });
          // setAudioUrls(prev => ({ ...prev, [_word]: { ...prev[_word], playing: false } }))
        }
      };
      audioElement.addEventListener("ended", handleEnded);
      return () => {
        audioElement.removeEventListener("ended", handleEnded);
      };
    }
  }, [audioPlayerRef]);

  useEffect(() => {
    if (questionText && currentText.current != questionText) {
      currentText.current = questionText;
      // Abort before stopping so the recorder's onstop releases the microphone
      // but publishes no blob. Clearing the chunk buffer (what this used to do)
      // did the opposite of what it intended: the final flush still landed in the
      // emptied array, so onstop produced a fragment with no container header —
      // small enough to look plausible, and rejected outright by the server.
      const liveSession = activeSessionRef.current;
      if (liveSession) liveSession.aborted = true;
      setAudioBlob(null);
      stopRecording(true);
      setEvaluating(false);
      setFailed(false);
      setFailures(0);
      // Reset the prevFailures guard when the question changes so threshold detection
      // starts fresh for the new question and doesn't immediately retrigger.
      try {
        prevFailures.current = 0;
      } catch (e) {
        // prevFailures may not exist in some contexts; ignore safely
      }
      setFailedWords([]);
      setLocalSuccessWords([]);
      setIsPlaying(false);
      setHasAudioLoaded(false);
      globalSetFailed(false);
      globalSetFailedWords([]);
      setSuccessWords([]);
      words.current = [];
      referenceId.current = generateUUID(); // Regenerate reference ID

      // Clear previous sentence audio state to prevent old audio from playing
      // simultaneously with the new sentence's audio after 3 failures.
      setSentanceAudio(null);
      setAudioUrls([]);
      setAudioLoading(false);
      setNeedsUserPlay(false);

      // Stop the live sentence audio so it doesn't bleed into the next question,
      // and detach onended/onerror so neither fires for the old question. Also
      // cancel the pronunciation watchdog so it can't advance a stale sentence.
      if (sentanceAudioRef.current) {
        sentanceAudioRef.current.pause();
        sentanceAudioRef.current.onended = null;
        sentanceAudioRef.current.onerror = null;
        sentanceAudioRef.current = null;
      }
      if (pronunciationWatchdogRef.current) {
        clearTimeout(pronunciationWatchdogRef.current);
        pronunciationWatchdogRef.current = null;
      }
      sentancePlayStartedRef.current = false;

      // Stop any currently playing audio from the previous sentence
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
      }
    }
  }, [
    questionText,
    stopRecording,
    globalSetFailed,
    globalSetFailedWords,
    setSuccessWords,
  ]);

  useEffect(() => {
    if (recording || isPlaying) {
      // Trigger animation when recording or audio is playing
      const interval = setInterval(() => {
        setCurrentWave((prev) => (prev === waveSvg ? wave2Svg : waveSvg));
      }, 500); // Switch every 500ms or adjust as needed

      return () => clearInterval(interval); // Clean up interval when neither recording nor playing
    } else {
      setCurrentWave(waveSvg); // Reset to the first wave when not recording or playing
    }
  }, [recording, isPlaying]);

  // Read the sentence for the user (TTS) — simplified: parent should provide TTS audio.
  // Child no longer performs socket or generates TTS itself. It will use the prefetched
  // audio provided by the parent via the `prefetchedSentanceAudio` prop, or notify the
  // parent via `onFailureThresholdReached` so the parent can generate/provide audio.
  useEffect(() => {
    // Only trigger when failures crosses the threshold from below to >= maxRetries.
    // This prevents duplicate runs if the effect re-runs without a real threshold crossing.
    const prev = prevFailures.current || 0;
    if (skipFailures || !(prev < maxRetries && failures >= maxRetries)) {
      // update prevFailures to current so subsequent changes are measured correctly
      prevFailures.current = failures;
      return;
    }
    // record that we've observed the crossing for this run
    prevFailures.current = failures;

    // If parent provided prefetched audio, use it directly
    if (prefetchedSentanceAudio) {
      console.log("prefetched audio: ", prefetchedSentanceAudio);
      setSentanceAudio(prefetchedSentanceAudio);
      return;
    }

    // If no prefetched audio is available, notify parent to generate/return audio.
    // Parent should call back (via onFailureThresholdReached) with audio or handle generation.
    if (
      onFailureThresholdReached &&
      typeof onFailureThresholdReached === "function"
    ) {
      try {
        console.log("existing not found");
        onFailureThresholdReached({
          meta: failedWords && failedWords.length ? failedWords : [],
          audio: audioBlob || null,
        });
      } catch (e) {
        console.error(
          "Error notifying parent for failure-audio generation:",
          e,
        );
      }
    }
  }, [
    failures,
    maxRetries,
    failedWords.length,
    skipFailures,
    questionText,
    question.index,
    isSentanceFailed,
    totalWords,
    onFailureThresholdReached,
    prefetchedSentanceAudio,
  ]);

  return (
    <Box sx={{ position: "relative", height: "40vh", background: "" }}>
      <Typography
        sx={{
          fontSize: {
            xs: "1em",
            sm: "1em",
            md: "1.4em",
            lg: "1.6em",
            xl: "2em",
          },
          fontFamily: '"Quicksand", sans-serif',
          fontWeight: "700",
          textAlign: "center",
        }}
      >
        {evaluating ? "Evaluate" : !recording ? "Click to Start" : "Read"}
      </Typography>
      {showTitle && (
        <Text
          content={
            title ||
            "Read aloud the following sentence and then we will evaluate your speech:"
          }
          style={{ marginBottom: "1rem" }}
        />
      )}
      {showText && (
        <>
          {questionText.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                sx={{
                  fontSize: "0.8em",
                  fontFamily: '"Quicksand", sans-serif',
                  fontWeight: "600",
                  textAlign: "center",
                  color: "#464255",
                  my: 2,
                  display: "inline-block",
                  position: "relative",
                }}
              >
                {failedWords.length > 0 && (
                  <div className={classes.failedCount}>
                    {failedWords.length}
                  </div>
                )}
                {(questionText.match(/\S+/g) || []).map(
                  (_txt, index) => {
                    // Check if this word is missed/failed/success.
                    // Missed (Omission) takes precedence over failed/success.
                    const isMissedWord = missedWords.some(
                      (mw) => mw.index === index,
                    );
                    const isFailedWord =
                      !isMissedWord && failedWords.includes(_txt);
                    const isSuccessWord =
                      !isMissedWord &&
                      localSuccessWords.some((sw) => sw.word === _txt);

                    let wordStyle = {};
                    if (isMissedWord) {
                      wordStyle = {
                        color: "#000000",
                        fontWeight: "700",
                        background: "rgba(0, 0, 0, 0.08)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        marginRight: "4px",
                      };
                    } else if (isFailedWord) {
                      wordStyle = {
                        color: "#FF3B30",
                        fontWeight: "700",
                        background: "rgba(255, 59, 48, 0.1)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        marginRight: "4px",
                      };
                    } else if (isSuccessWord) {
                      wordStyle = {
                        color: "#3BCF00",
                        fontWeight: "700",
                        background: "rgba(59, 207, 0, 0.1)",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        marginRight: "4px",
                      };
                    } else {
                      wordStyle = { marginRight: "4px" };
                    }

                    let synonym = dictionary?.find(
                      (s) => s.text.toLowerCase() == _txt.toLowerCase(),
                    );

                    if (synonym && Object.keys(synonym).length) {
                      if (synonym.definition && synonym.synonyms?.length) {
                        title = (
                          <Box>
                            <Typography
                              sx={{
                                color: "#000",
                                fontSize: "0.8em",
                                fontFamily: '"Quicksand", sans-serif',
                                fontWeight: "700",
                              }}
                            >
                              <span style={{ color: "#9E6AFF" }}>{_txt}</span>:
                              {synonym.definition}
                            </Typography>
                            <Typography
                              sx={{
                                color: "#000",
                                fontSize: "0.8em",
                                fontFamily: '"Quicksand", sans-serif',
                                fontWeight: "600",
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>Synonyms</span>:
                              {synonym.synonyms.join(", ")}
                            </Typography>
                          </Box>
                        );
                      } else if (
                        synonym.synonyms?.length &&
                        !synonym.definition
                      ) {
                        title = (
                          <Box>
                            <Typography
                              sx={{
                                color: "#000",
                                fontSize: "0.8em",
                                fontFamily: '"Quicksand", sans-serif',
                                fontWeight: "700",
                              }}
                            >
                              <span style={{ fontWeight: 700 }}>Synonyms</span>:
                              {synonym.synonyms.join(", ")}
                            </Typography>
                          </Box>
                        );
                      } else if (
                        !synonym.synonyms?.length &&
                        synonym.definition
                      ) {
                        title = (
                          <Box>
                            <Typography
                              sx={{
                                color: "#000",
                                fontSize: "0.8em",
                                fontFamily: '"Quicksand", sans-serif',
                                fontWeight: "700",
                              }}
                            >
                              <span style={{ color: "#9E6AFF" }}>{_txt}</span>:
                              {synonym.definition}
                            </Typography>
                          </Box>
                        );
                      }
                    }

                    return (
                      <span key={index} style={wordStyle}>
                        {_txt}
                      </span>
                    );
                  },
                )}
              </Typography>
            </Box>
          )}
        </>
      )}

      <Box>
        <Typography
          sx={{
            fontSize: {
              xs: "1em",
              sm: "1em",
              md: "1.4em",
              lg: "1.6em",
              xl: "2em",
            },
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: "700",
            textAlign: "center",
          }}
        >
          {formatTime(elapsedTime)}
        </Typography>
        {recording && (
          <Box
            sx={{
              position: "relative",
              textAlign: "center",
              p: 1,
            }}
          >
            <Typography
              sx={{
                color: "#464255",
                fontSize: {
                  xs: "0.6em",
                  sm: "0.6em",
                  md: "0.8em",
                  lg: "1em",
                  xl: "1.2em",
                },
                fontFamily: '"Quicksand", sans-serif',
                fontWeight: "700",
                textAlign: "center",
                m: 1,
                position: "absolute",
                top: "-25vh",
                left: "50%",
                width: "100%",
                transform: "translateX(-50%)",
              }}
            >
              Finish Reading! Click ‘
              <span style={{ color: "#3BCF00" }}>Done</span>’.
            </Typography>
            <img
              style={{
                width: "40%",
                zIndex: 1,
                position: "absolute",
                top: "10px",
                left: "50%",
                transform: "translateX(-50%)",
              }}
              src={recordListenSvg}
              alt=""
            />
            <img
              ref={svgRef}
              style={{
                width: "100%",
                transition: "transform 0.1s ease",
                display: recording ? "block" : "none",
                position: "absolute",
                zIndex: 0,
                top: "20px",
              }}
              src={currentWave}
              alt="Audio Wave"
            />
            <Box
              sx={{
                position: "absolute",
                top: "24vh",
                left: "50%",
                transform: "translateX(-50%)",
                background:
                  "linear-gradient(0deg, #CCCCCC -2.68%, #E6E6E6 31.68%, #F8F8F8 64.23%, #FFFFFF 87.74%)",
                padding: "5px",
                borderRadius: "50px",
              }}
            >
              <Button
                sx={{
                  background:
                    "linear-gradient(to top, #309906, #32a305, #34ad04, #36b703, #38c102, #3ec909, #44d110, #4ad916, #54e121, #5ee92a, #68f132, #71f93a)",
                  padding: "10px 20px",
                  borderRadius: "50px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  "&:hover": {
                    background:
                      "linear-gradient(to top, #287d05, #2a8804, #2c9203, #2e9c02, #30a501, #35ae08, #3ab70e, #40bf15, #4ac71f, #55cf28, #5fd731, #69df3a)",
                  },
                }}
                onClick={() => stopRecording()}
              >
                {/* {tickSvg} */}
                <img style={{ width: "30%" }} src={tickSvg} alt="" />
                <Typography
                  sx={{
                    fontSize: "1.2em",
                    fontFamily: '"Quicksand", sans-serif',
                    fontWeight: "600",
                    textAlign: "center",
                    color: "#ffffff",
                  }}
                >
                  Done
                </Typography>
              </Button>
            </Box>
          </Box>
        )}
        {evaluating && (
          <Box
            sx={{
              position: "relative",
              textAlign: "center",
              p: 1,
            }}
          >
            <img
              style={{
                width: "40%",
                zIndex: 1,
                position: "absolute",
                top: "20px",
                left: "50%",
                transform: "translateX(-50%)",
              }}
              src={recordMicSvg}
              alt=""
            />
            <img
              style={{
                width: "50%",
                transition: "transform 0.1s ease",
                // display: evaluating ? 'block' : 'none',
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 0,
                top: "0px",
              }}
              src={evalBorderSvg}
              alt=""
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "50%",
                height: `100%`,
                background:
                  "linear-gradient(180deg, rgba(68, 122, 226, 0) 0.65%, rgba(68, 122, 226, 0.2) 41%, #447AE2 78.24%)",
                animation: "scan 3s linear infinite",
                zIndex: 2,
              }}
            ></Box>
            <style>
              {`
                @keyframes scan {
                0% {
                  transform: translateX(-50%) translateY(0%);
                }
                50% {
                  transform: translateX(-50%) translateY(800%);
                }
                100% {
                  transform: translateX(-50%) translateY(0%);
                }
              }
            `}
            </style>
          </Box>
        )}
        {!recording &&
          !evaluating &&
          !isPlaying &&
          !hasAudioLoaded &&
          !audioLoading &&
          (failed || networkError || failedWords.length > 0) && (
            <Box
              sx={{
                position: "relative",
                textAlign: "center",
                p: 1,
              }}
            >
              <Button
                sx={{
                  width: "100%",
                  padding: 0,
                  margin: 0,
                }}
                // Wrapped, not passed directly: startRecording's first argument
                // is `force`, and handing it the click event made every click a
                // forced start.
                onClick={() => startRecording()}
              >
                <img
                  style={{
                    width: "40%",
                    zIndex: 1,
                    position: "absolute",
                    top: "0px",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                  src={replayMicSvg}
                  alt=""
                />
              </Button>
            </Box>
          )}
        {!recording &&
          !evaluating &&
          !isPlaying &&
          !hasAudioLoaded &&
          !audioLoading &&
          !(failed || networkError || failedWords.length > 0) && (
            <Box
              sx={{
                position: "relative",
                textAlign: "center",
                p: 1,
              }}
            >
              <Button
                sx={{
                  width: "100%",
                  padding: 0,
                  margin: 0,
                }}
                // Wrapped, not passed directly: startRecording's first argument
                // is `force`, and handing it the click event made every click a
                // forced start.
                onClick={() => startRecording()}
              >
                <img
                  style={{
                    width: "40%",
                    zIndex: 1,
                    position: "absolute",
                    top: "0px",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                  src={readSvg}
                  alt=""
                />
              </Button>
            </Box>
          )}
        {hasAudioLoaded && (
          <Box
            sx={{
              position: "relative",
              textAlign: "center",
              p: 1,
              // The base + wave images are layered with absolute positioning, so
              // this Box collapses to zero content height. Give it a real height so
              // the pronunciation-replay button stays visible and clickable (esp.
              // the autoplay-blocked "tap to hear the correct pronunciation" case).
              minHeight: 120,
            }}
          >
            <Button
              sx={{
                width: "100%",
                padding: 0,
                margin: 0,
              }}
              onClick={() => {
                // Replay within a user gesture — covers the autoplay-blocked case.
                setNeedsUserPlay(false);
                playSentanceAudio(
                  sentanceAudio,
                  evaluated,
                  audioBlob,
                  failures,
                );
              }}
            >
              <img
                style={{
                  width: "40%",
                  zIndex: 1,
                  position: "absolute",
                  top: "0px",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
                src={systemSpeakSvg}
                alt=""
              />
              <img
                ref={svgRef}
                style={{
                  width: "100%",
                  transition: "transform 0.1s ease",
                  display: isPlaying ? "block" : "none",
                  position: "absolute",
                  zIndex: 0,
                  top: "5px",
                }}
                src={currentWave}
                alt="Audio Wave"
              />
            </Button>
            {needsUserPlay && (
              <div style={styles.statusMessage}>
                Tap to hear the correct pronunciation
              </div>
            )}
          </Box>
        )}
        {audioLoading && (
          <div style={styles.statusMessage}>Loading Pronunciation...</div>
        )}
      </Box>
      <RecordingInactivity
        open={inactivity}
        close={setInactivityPrompt}
        onContinue={handleInactivityContinue}
        onPause={handleCoursePause}
        title={INACTIVITY_COPY[inactivityReason].title}
        message={INACTIVITY_COPY[inactivityReason].message}
      />
      <audio src={audioURL} style={{ display: "none" }} ref={audioPlayerRef} />
    </Box>
  );
};

const styles = {
  container: {
    marginTop: "20px",
    marginBottom: "20px",
    padding: "10px",
  },
  timer: {
    marginBottom: "20px",
    marginTop: "1rem",
    fontWeight: "500",
    fontSize: "2.1rem",
    textAlign: "center",
  },
  actionBtnContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "1rem",
  },
  recButton: {
    width: "90px",
    height: "58px",
    borderRadius: "9999px",
  },
  responseItems: {
    marginTop: "10px",
    marginBottom: "10px",
  },
  correct: {
    color: "green",
  },
  wrong: {
    color: "red",
    fontWeight: "700",
  },
  evaluationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    marginBottom: "20px",
  },
  statusMessage: {
    marginTop: "1rem",
    textAlign: "center",
  },
  importantWord: {
    color: "rgba(158, 106, 255, 1)",
    cursor: "pointer",
  },
};

export default SpeechEvaluation;
